import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Building2, Calendar, ArrowRight, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chrome background from envelope page
// Default gradient background (no hardcoded images)
const DEFAULT_GRADIENT = "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)";

// Helper to get image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API}${path}`;
};

// Timezone-safe date formatting
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split('T')[0].split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { 
    weekday: "long", 
    month: "long", 
    day: "numeric", 
    year: "numeric" 
  });
};

// Format time to 12-hour format
const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const JoinPartnerPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState("loading"); // loading, valid, accepting, success, error
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setStatus("loading");
      const res = await fetch(`${API}/join/partner/${token}`);
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Invalid invitation");
      }
      
      const data = await res.json();
      setInviteData(data);
      setStatus("valid");
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  const acceptInvitation = async () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      localStorage.setItem("join_redirect", `/join/partner/${token}`);
      navigate("/login");
      return;
    }

    try {
      setStatus("accepting");
      const sessionToken = localStorage.getItem("session_token");
      
      const res = await fetch(`${API}/join/partner/${token}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionToken}`
        }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to accept invitation");
      }
      
      const data = await res.json();
      setStatus("success");
      toast.success("Organization linked successfully!");
      
      // Redirect to profile or partner portal
      setTimeout(() => {
        navigate(data.redirect_url || "/me");
      }, 2000);
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  const partnerTypeLabels = {
    sponsor: "Sponsor",
    media: "Media Partner",
    partner: "Partner Organization"
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        
        {/* Loading */}
        {status === "loading" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-gray-900 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Verifying invitation...</h1>
          </Card>
        )}

        {/* Valid Invitation */}
        {status === "valid" && inviteData && (
          <Card className="overflow-hidden">
            {/* Event Cover - Use event cover or gradient (no hardcoded images) */}
            <div 
              className="relative aspect-square w-full max-h-48 overflow-hidden"
              style={inviteData.event?.cover_image 
                ? { backgroundImage: `url(${getImageUrl(inviteData.event.cover_image)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : { background: DEFAULT_GRADIENT }
              }
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Logo overlay */}
              {inviteData.event?.logo_image && (
                <div className="absolute bottom-3 left-3">
                  <div className="w-12 h-12 rounded-lg bg-white p-1 shadow-lg">
                    <img 
                      src={getImageUrl(inviteData.event.logo_image)} 
                      alt="" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <CardContent className="p-6 space-y-5">
              {/* Invitation Header */}
              <div className="text-center space-y-2">
                <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">
                  Partner Invitation
                </h1>
                <p className="text-gray-600 text-sm">
                  Your organization has been added as a partner for
                </p>
              </div>

              {/* Event Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h2 className="font-semibold text-lg text-gray-900">{inviteData.event?.name}</h2>
                
                {/* Date & Time */}
                {inviteData.event?.date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {formatDate(inviteData.event.date)}
                      {inviteData.event.start_time && (
                        <span className="ml-1">
                          • {formatTime(inviteData.event.start_time)}
                          {inviteData.event.timezone_abbr && ` ${inviteData.event.timezone_abbr}`}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                
                {/* Location */}
                {(inviteData.event?.venue_name || inviteData.event?.city) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {inviteData.event.venue_name}
                      {inviteData.event.venue_name && inviteData.event.city && ", "}
                      {inviteData.event.city}
                    </span>
                  </div>
                )}
              </div>

              {/* Organization Info */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm text-gray-500 mb-1">Your Organization</p>
                <p className="font-semibold text-lg text-gray-900">{inviteData.org_name}</p>
                <p className="text-sm text-blue-600 capitalize mt-1">
                  {partnerTypeLabels[inviteData.partner_type] || inviteData.partner_type}
                </p>
              </div>

              {/* What you get */}
              <div className="text-sm text-gray-600 space-y-2">
                <p className="font-medium text-gray-800">As a partner you can:</p>
                <ul className="space-y-1 pl-4 list-disc">
                  <li>Update your organization profile</li>
                  <li>See how you appear on event pages</li>
                  <li>Receive event analytics (coming soon)</li>
                </ul>
              </div>

              {/* Invited As */}
              {inviteData.contact_email && (
                <div className="text-center text-sm text-gray-500">
                  Invitation sent to: <span className="font-medium">{inviteData.contact_email}</span>
                </div>
              )}

              {/* CTA */}
              <Button 
                onClick={acceptInvitation}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3"
                data-testid="accept-partner-invite-btn"
              >
                {isAuthenticated ? (
                  <>
                    Accept & Link Organization
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Sign In to Accept
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              {isAuthenticated && user?.email !== inviteData.contact_email && (
                <p className="text-xs text-center text-amber-600">
                  Signed in as {user?.email}. The invitation was sent to a different email.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Accepting */}
        {status === "accepting" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-gray-900 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Linking organization...</h1>
          </Card>
        )}

        {/* Success */}
        {status === "success" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Organization Linked!</h1>
            <p className="text-gray-600 mt-2">Redirecting to your profile...</p>
          </Card>
        )}

        {/* Error */}
        {status === "error" && (
          <Card className="text-center p-8">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Invitation Error</h1>
            <p className="text-gray-600 mt-2">{error || "This invitation link is invalid or has expired."}</p>
            <Button 
              onClick={() => navigate("/")}
              variant="outline"
              className="mt-4"
            >
              Go Home
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default JoinPartnerPage;
